/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiPrinter } from 'react-icons/fi';
import { getVisaApplicationByIdApi } from '../../api/onlineVisaApi';
import { formatPrice, formatEntry } from '../../utils/onlineVisaUtils';
import { VISA_PAX_TYPES } from '../../constants/onlineVisaConstants';
import './OnlineVisaInvoicePage.scss';

const PAX_LABEL = {
  [VISA_PAX_TYPES.ADULT]: 'Adult',
  [VISA_PAX_TYPES.CHILD]: 'Child',
  [VISA_PAX_TYPES.INFANT]: 'Infant',
};

function titleCase(str) {
  if (!str) return '';
  return String(str)
    .toLowerCase()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function safe(value, fallback = '—') {
  if (value === null || value === undefined || value === '') return fallback;
  return value;
}

function formatInvoiceDate(value) {
  if (!value) return '—';
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const dd = String(d.getDate()).padStart(2, '0');
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const mm = monthNames[d.getMonth()];
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  } catch (e) {
    return String(value);
  }
}

function OnlineVisaInvoicePage() {
  const navigate = useNavigate();
  const { transactionId } = useParams();
  const txId = decodeURIComponent(transactionId || '');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!txId) {
      setLoading(false);
      setError('Missing transaction id in URL.');
      return () => {};
    }
    setLoading(true);
    setError('');
    (async () => {
      const result = await getVisaApplicationByIdApi(txId);
      if (cancelled) return;
      if (!result) {
        setError('Unable to load the invoice. Please try again.');
        setLoading(false);
        return;
      }
      setData(result);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [txId]);

  const agent = data?.agent || {};
  const application = data?.application || {};
  const applicants = Array.isArray(data?.applicants) ? data.applicants : [];
  const visaInvoice = useMemo(() => data?.visaInvoice || {}, [data]);
  const paxFareBreakup = useMemo(() => {
    try {
      if (
        visaInvoice?.farebreakupstring &&
        JSON.parse(visaInvoice.farebreakupstring)?.visaFare?.paxWisePriceList
      ) {
        const paxBreakup = JSON.parse(visaInvoice.farebreakupstring).visaFare
          .paxWisePriceList;
        return paxBreakup.reduce((acc, curr) => {
          if (curr.paxType) acc[curr.paxType] = curr;
          return acc;
        }, {});
      }
      return {};
    } catch (e) {
      return {};
    }
  }, [visaInvoice]);

  const currency = application?.currency || 'INR';
  const totalAmount = Number(visaInvoice?.totalinvoice) || 0;

  const invoiceNo =
    visaInvoice?.txid || application?.application_ref_no || txId || '—';
  const invoiceDate = formatInvoiceDate(
    visaInvoice?.txdate || application?.created_on,
  );

  const billedTo = {
    name:
      [agent?.salutation, agent?.first_name, agent?.last_name]
        .filter(Boolean)
        .join(' ') ||
      agent?.userid ||
      'Agent',
    agentId: agent?.agentid || '—',
    email: agent?.contact_email || agent?.email || '—',
    phone: agent?.mobile_number || agent?.phone || '',
  };

  const visaDetails = {
    destination:
      titleCase(application?.country_to || application?.destination_name) ||
      '—',
    visaType: visaInvoice?.typeofvisa || application?.visa_name || '—',
    entry: formatEntry(application?.entry) || '—',
    validity: application?.validity
      ? `${application.validity} ${application.validity_type || ''}`.trim()
      : '—',
    stay: application?.duration
      ? `${application.duration} ${application.duration_type || ''}`.trim()
      : '—',
    adults: application?.total_adults != null ? application.total_adults : '0',
    children: application?.total_child != null ? application.total_child : '0',
    infants:
      application?.total_infants != null ? application.total_infants : '0',
  };

  const handleBack = () => navigate(-1);
  const handlePrint = () => {
    if (typeof window !== 'undefined') {
      window.print();
    }
  };

  if (loading) {
    return (
      <main className="invoice-page">
        <Container>
          <div className="invoice-page__loader">Loading invoice…</div>
        </Container>
      </main>
    );
  }

  if (error || !data) {
    return (
      <main className="invoice-page">
        <Container>
          <div className="invoice-page__error">
            {error || 'Unable to load the invoice.'}
          </div>
          <button
            type="button"
            className="invoice-page__back-btn"
            onClick={handleBack}
          >
            <FiArrowLeft />
            <span>Back</span>
          </button>
        </Container>
      </main>
    );
  }

  return (
    <main className="invoice-page">
      <Container>
        {/* TOP ACTIONS (hidden in print) */}
        <div className="invoice-page__actions">
          <button
            type="button"
            className="invoice-page__back-btn"
            onClick={handleBack}
          >
            <FiArrowLeft />
            <span>Back</span>
          </button>
          <div className="invoice-page__actions-right">
            <button
              type="button"
              className="invoice-page__print-btn"
              onClick={handlePrint}
            >
              <FiPrinter />
              <span>Print</span>
            </button>
            {/* <button
              type="button"
              className="invoice-page__download-btn"
              onClick={handlePrint}
            >
              <FiDownload />
              <span>Download PDF</span>
            </button> */}
          </div>
        </div>

        {/* INVOICE PAPER */}
        <article className="invoice-page__paper">
          {/* Header */}
          <header className="invoice-page__header">
            <div className="invoice-page__brand">
              <div className="invoice-page__logo" aria-hidden>
                IVS
              </div>
              <div className="invoice-page__brand-meta">
                <strong>IWeen Visa Services</strong>
                <span>Online Visa Booking</span>
              </div>
            </div>
            <h1 className="invoice-page__title">INVOICE</h1>
          </header>

          {/* Billed to + Invoice meta */}
          <section className="invoice-page__intro">
            <div className="invoice-page__billed-to">
              <h3 className="invoice-page__block-title">Billed To</h3>
              <p className="invoice-page__billed-name">{billedTo.name}</p>
              <p className="invoice-page__billed-row">
                <span>Agent ID</span>
                <strong>{billedTo.agentId}</strong>
              </p>
              <p className="invoice-page__billed-row">
                <span>Email</span>
                <strong>{billedTo.email}</strong>
              </p>
              {billedTo.phone && (
                <p className="invoice-page__billed-row">
                  <span>Phone</span>
                  <strong>{billedTo.phone}</strong>
                </p>
              )}
            </div>

            <div className="invoice-page__invoice-meta">
              <div className="invoice-page__meta-row">
                <span>Invoice No</span>
                <strong>{invoiceNo}</strong>
              </div>
              <div className="invoice-page__meta-row">
                <span>Invoice Date</span>
                <strong>{invoiceDate}</strong>
              </div>
              <div className="invoice-page__meta-row">
                <span>Application Id</span>
                <strong>
                  {safe(application?.visa_application_id || txId)}
                </strong>
              </div>
              <div className="invoice-page__meta-row">
                <span>Payment Type</span>
                <strong>
                  {safe(visaInvoice?.txtype, 'DEPOSIT')
                    .toString()
                    .toUpperCase()}
                </strong>
              </div>
              <div className="invoice-page__meta-row">
                <span>Status</span>
                <strong className="invoice-page__status">
                  {(
                    visaInvoice?.bookingStatus ||
                    application?.booking_status ||
                    'CONFIRMED'
                  )
                    .toString()
                    .toUpperCase()}
                </strong>
              </div>
            </div>
          </section>

          {/* Visa Details */}
          <section className="invoice-page__section">
            <h3 className="invoice-page__block-title">Visa Details</h3>
            <div className="invoice-page__visa-grid">
              <div className="invoice-page__detail">
                <span>Destination</span>
                <strong>{visaDetails.destination}</strong>
              </div>
              <div className="invoice-page__detail">
                <span>Visa Type</span>
                <strong>{visaDetails.visaType}</strong>
              </div>
              <div className="invoice-page__detail">
                <span>Entry</span>
                <strong>{visaDetails.entry}</strong>
              </div>
              <div className="invoice-page__detail">
                <span>Validity</span>
                <strong>{visaDetails.validity}</strong>
              </div>
              <div className="invoice-page__detail">
                <span>Stay Duration</span>
                <strong>{visaDetails.stay}</strong>
              </div>
              <div className="invoice-page__detail">
                <span>Adults</span>
                <strong>{visaDetails.adults}</strong>
              </div>
              <div className="invoice-page__detail">
                <span>Children</span>
                <strong>{visaDetails.children}</strong>
              </div>
              <div className="invoice-page__detail">
                <span>Infants</span>
                <strong>{visaDetails.infants}</strong>
              </div>
            </div>
          </section>

          {/* Applicants */}
          <section className="invoice-page__section">
            <h3 className="invoice-page__block-title">
              Applicants ({applicants.length})
            </h3>
            <div className="invoice-page__table-wrap">
              <table className="invoice-page__table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Date of Birth</th>
                    <th>Passport No</th>
                    <th>Nationality</th>
                  </tr>
                </thead>
                <tbody>
                  {applicants.map((ap, idx) => (
                    <tr key={ap.applicant_id || idx}>
                      <td>{idx + 1}</td>
                      <td>
                        {[ap.title, ap.first_name, ap.last_name]
                          .filter(Boolean)
                          .join(' ') || '—'}
                        {ap.primary_applicant === 'Y' ||
                        ap.primary_applicant === true
                          ? ' (Lead)'
                          : ''}
                      </td>
                      <td>{PAX_LABEL[ap.pax_type] || ap.pax_type || '—'}</td>
                      <td>{formatInvoiceDate(ap.date_of_birth)}</td>
                      <td>{safe(ap.passport_number)}</td>
                      <td>
                        {safe(
                          ap.nationality || visaInvoice?.paxnationality,
                          'IN',
                        )}
                      </td>
                    </tr>
                  ))}
                  {applicants.length === 0 && (
                    <tr>
                      <td colSpan="6" className="invoice-page__empty">
                        No applicants on this booking.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* Pricing Breakdown */}
          <section className="invoice-page__section">
            <h3 className="invoice-page__block-title">Pricing Breakdown</h3>
            <div className="invoice-page__table-wrap">
              <table className="invoice-page__table invoice-page__table--pricing">
                <thead>
                  <tr>
                    <th>Pax Type</th>
                    <th className="invoice-page__num">Count</th>
                    <th className="invoice-page__num">Base Fare</th>
                    <th className="invoice-page__num">Tax</th>
                    <th className="invoice-page__num">Handling Charges</th>
                    <th className="invoice-page__num">Comission</th>
                    <th className="invoice-page__num">TDS</th>
                    <th className="invoice-page__num">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(paxFareBreakup).map((key) => {
                    const pw = paxFareBreakup[key];
                    const baseFare = Number(pw.paxTotalAmount || 0);
                    const tax = Number(
                      pw.PaxHandleCharges + pw.PaxTfeeInv || 0,
                    );
                    const handlingCharges = Number(
                      pw.PaxHiddenMarkupOnBaseFare + pw.hiddenMarkup || 0,
                    );
                    const comission = Number(pw.paxCommission || 0);
                    const tds = Number(pw.paxTds || 0);
                    const totalNet = Number(pw.PaxInvoiceTotal || 0);
                    // const subtotal = Number(pw.PaxFareQuoteTotal || 0);
                    return (
                      <tr key={key}>
                        <td>{PAX_LABEL[pw.paxType] || pw.paxType}</td>
                        <td className="invoice-page__num">
                          {pw.paxCount || 1}
                        </td>
                        <td className="invoice-page__num">
                          {formatPrice(baseFare, currency) ||
                            `${currency} ${baseFare}`}
                        </td>
                        <td className="invoice-page__num">
                          {formatPrice(tax, currency) || `${currency} ${tax}`}
                        </td>
                        <td className="invoice-page__num">
                          {formatPrice(handlingCharges, currency) ||
                            `${currency} ${handlingCharges}`}
                        </td>
                        <td className="invoice-page__num">
                          {formatPrice(comission, currency) ||
                            `${currency} ${comission}`}
                        </td>
                        <td className="invoice-page__num">
                          {formatPrice(tds, currency) || `${currency} ${tds}`}
                        </td>
                        <td className="invoice-page__num">
                          <strong>
                            {formatPrice(totalNet, currency) ||
                              `${currency} ${totalNet}`}
                          </strong>
                        </td>
                      </tr>
                    );
                  })}
                  {Object.keys(paxFareBreakup).length === 0 && (
                    <tr>
                      <td colSpan="6" className="invoice-page__empty">
                        Pricing breakdown is not available.
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="7" className="invoice-page__grand-label">
                      Grand Total
                    </td>
                    <td className="invoice-page__num invoice-page__grand-value">
                      {formatPrice(totalAmount, currency) ||
                        `${currency} ${totalAmount}`}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          {/* Footer */}
          <footer className="invoice-page__footer">
            <p>
              This is a computer-generated invoice and does not require a
              signature.
            </p>
            <p>
              For any queries regarding this invoice, please contact your travel
              agent or the visa services helpdesk.
            </p>
          </footer>
        </article>
      </Container>
    </main>
  );
}

export default OnlineVisaInvoicePage;
